import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, Package, ChevronLeft, Check, Share2, Video, Users, FileText, ChevronDown } from 'lucide-react';
import { Product } from '../types';
import { supabase } from '../lib/supabaseClient';
import { mapDbProductRow } from '../utils/mapDbProduct';
import { formatExactText } from '../utils/textFormatter';
import { Helmet } from 'react-helmet-async';
import ShareProductModal from '../components/ShareProductModal';
import VideoPlayer from '../components/VideoPlayer';
import ProductSchema from '../components/ProductSchema';
import RelatedProducts from '../components/RelatedProducts';
import SocialShareButtons from '../components/SocialShareButtons';
import SocialShareWidget from '../components/SocialShareWidget';
import CustomerQuoteRequestForm from '../components/CustomerQuoteRequestForm';
import PriceWithVatLabel from '../components/PriceWithVatLabel';
import { getProductSeoTitle } from '../seo/productSeoTitles';
import { getProductCannibalizationHint } from '../seo/productCannibalization';
import { applyProductSeoMetaToDocument } from '../utils/productSeoMeta';
import ProductNewRibbon from '../components/ProductNewRibbon';
import { trackViewItem } from '../utils/ga4Ecommerce';
import { trackBookingCtaClick, trackProductView, trackQuoteCtaClick } from '../utils/siteAnalytics';
import ProductScenFaqSection from '../components/ProductScenFaqSection';
import PendingPromoBanner from '../components/PendingPromoBanner';
import { peekPendingDiscountCode } from '../utils/rebookPromo';
import {
  HOME_BOOKING_EVENT_TYPES,
  readHomeBookingIntent,
  type HomeBookingEventType,
} from '../utils/homeBookingIntent';

const SCEN_TROSS_CATEGORY = 'Scen / Tross';

const ProductDetailPage: React.FC = () => {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [hasPendingPromo] = useState(() => Boolean(peekPendingDiscountCode()));

  const quotePrefill = React.useMemo(() => {
    const session = readHomeBookingIntent();
    const date = searchParams.get('date') || session?.date || undefined;
    const dateUndecided =
      searchParams.get('dateUndecided') === '1' || Boolean(session?.dateUndecided && !date);
    const eventRaw = searchParams.get('event') || session?.event || '';
    const defaultEventType = (HOME_BOOKING_EVENT_TYPES as readonly string[]).includes(eventRaw)
      ? (eventRaw as HomeBookingEventType)
      : undefined;
    return { defaultEventDate: date, defaultDateUndecided: dateUndecided, defaultEventType };
  }, [searchParams]);
  useEffect(() => {
    // Signal to prerender that content is not ready yet
    if (typeof window !== 'undefined') {
      (window as any).prerenderReady = false;
    }

    // Set timeout to ensure prerender doesn't wait indefinitely
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') {
        (window as any).prerenderReady = true;
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [idOrSlug]);

  // Sync title + description into the document *before* prerenderReady so Netlify
  // snapshots page-unique meta (Helmet alone can lose the race vs ready=true).
  // Same pattern as BlogPostPage.
  useEffect(() => {
    if (loading) return;

    if (product) {
      setSelectedImage(product.image);
      setSelectedVideoIndex(0);
      applyProductSeoMetaToDocument(product);
    }

    if (typeof window !== 'undefined') {
      (window as any).prerenderReady = true;
    }
  }, [loading, product]);

  useEffect(() => {
    if (!product) return;
    trackViewItem(product);
    trackProductView(product);
  }, [product?.id]);

  const fetchProduct = async () => {
    if (typeof window !== 'undefined') {
      (window as any).prerenderReady = false;
    }

    try {
      let query = supabase.from('products').select('*');
      
      // Check if idOrSlug is a UUID or a slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug || '');
      
      if (isUuid) {
        query = query.eq('id', idOrSlug);
      } else {
        query = query.eq('slug', idOrSlug);
      }
      
      const { data, error } = await query.maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        const transformedProduct = mapDbProductRow(data as Record<string, unknown>);

        // Write meta synchronously in the fetch path so prerender sees it even if
        // the effect below has not flushed yet (xsl12 lost this race: unique title,
        // leftover homepage description).
        applyProductSeoMetaToDocument(transformedProduct);
        setProduct(transformedProduct);
      }
    } catch (err: any) {
      console.error('Error fetching product:', err);
      setError('Ett fel uppstod när produkten skulle hämtas');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };
  
  if (loading) {
    return (
      <div className="pb-16 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pb-16 min-h-screen">
        <Helmet>
          <title>Produkten kunde inte laddas | festutrustning.se</title>
        </Helmet>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Produkten kunde inte laddas</h1>
          <p className="text-gray-600 mb-6">
            Vi kunde inte hämta produkten just nu. Försök igen om en stund.
          </p>
          <Link
            to="/produkter"
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors duration-300"
          >
            <ChevronLeft className="mr-2" size={18} />
            Tillbaka till produkter
          </Link>
        </div>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="pb-16 min-h-screen">
        <Helmet>
          <title>Produkten hittades inte | festutrustning.se</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Produkten hittades inte</h1>
          <p className="text-gray-600 mb-6">Den produkt du söker verkar inte finnas i vårt sortiment.</p>
          <Link 
            to="/produkter" 
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors duration-300"
          >
            <ChevronLeft className="mr-2" size={18} />
            Tillbaka till produkter
          </Link>
        </div>
      </div>
    );
  }
  
  const productUrl = `${window.location.origin}/produkter/${product.slug || product.id}`;
  const hasVideos = product.videos && product.videos.length > 0;
  const productSeoTitle = getProductSeoTitle(
    product.slug,
    `${product.name} - ${product.priceOnRequest ? 'Begär offert' : 'Hyra festutrustning'} | festutrustning.se`,
  );
  const productSeoDescription = product.shortDescription?.trim()
    || (product.priceOnRequest
      ? `Begär offert för ${product.name}. Professionell festutrustning i Skåne med leverans.`
      : `Hyr ${product.name} för ${product.pricePerDay} kr/dag. Professionell festutrustning i Skåne med leverans.`);
  const cannibalizationHint = getProductCannibalizationHint(product.slug);
  
  return (
    <div className="pb-16 min-h-screen bg-transparent relative overflow-hidden">
      {/* Animated background elements */}
      <div className="hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating particles */}
      <div className="hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-30 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>
      
      <Helmet>
        <title>{productSeoTitle}</title>
        <meta name="description" content={productSeoDescription} />
        <link rel="canonical" href={`https://festutrustning.se/produkter/${product.slug || product.id}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://festutrustning.se/produkter/${product.slug || product.id}`} />
        <meta property="og:title" content={productSeoTitle.replace(' | festutrustning.se', '')} />
        <meta property="og:description" content={productSeoDescription} />
        {product.image && <meta property="og:image" content={product.image} />}
        {product.image && <meta property="og:image:width" content="1200" />}
        {product.image && <meta property="og:image:height" content="630" />}
        <meta property="og:site_name" content="festutrustning.se" />
        <meta property="og:locale" content="sv_SE" />
        {!product.priceOnRequest && <meta property="product:price:amount" content={product.pricePerDay.toString()} />}
        {!product.priceOnRequest && <meta property="product:price:currency" content="SEK" />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={productSeoTitle.replace(' | festutrustning.se', '')} />
        <meta name="twitter:description" content={productSeoDescription} />
        {product.image && <meta name="twitter:image" content={product.image} />}
      </Helmet>
      <ProductSchema product={product} />
      <div className="container mx-auto px-4">
        <div className="mb-6 relative z-10">
          <button
            type="button"
            onClick={() => {
              // Behåll kategori/sök från listans historikpost (?category= / ?search=)
              if (typeof window !== 'undefined' && window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/produkter');
              }
            }}
            className="inline-flex items-center text-purple-300 hover:text-purple-200 font-medium transition-colors"
          >
            <ChevronLeft size={18} className="mr-1" />
            Tillbaka till produkter
          </button>
        </div>
        
        <div className="bg-slate-900/35 border border-slate-800 overflow-hidden relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="p-6 lg:p-8">
              <div className="bg-slate-800/25 border border-slate-700 overflow-visible mb-4">
                <div className="relative pt-[100%]">
                  {showVideos && hasVideos ? (
                    <div className="absolute inset-0 w-full h-full overflow-hidden">
                      <VideoPlayer
                        url={product.videos![Math.min(selectedVideoIndex, product.videos!.length - 1)]}
                        className="w-full h-full"
                      />
                    </div>
                  ) : (
                    <div className="absolute inset-0 overflow-hidden">
                      <img 
                        src={selectedImage || product.image} 
                        alt={`${product.name} - Professionell festutrustning för uthyrning`}
                        className="absolute inset-0 w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {!showVideos && <ProductNewRibbon newUntil={product.newUntil} size="lg" />}
                </div>
              </div>
              
              <div className="grid grid-cols-5 gap-2">
                {/* Media type selector */}
                <div className="col-span-5 flex space-x-2 mb-2">
                  <button
                    onClick={() => setShowVideos(false)}
                    className={`flex-1 py-2 px-3 rounded-none text-sm font-medium ${
                      !showVideos 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Bilder
                  </button>
                  {hasVideos && (
                    <button
                      onClick={() => setShowVideos(true)}
                      className={`flex-1 py-2 px-3 rounded-none text-sm font-medium ${
                        showVideos 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Videos
                    </button>
                  )}
                </div>
                
                {!showVideos ? (
                  // Image thumbnails
                  <>
                    <div 
                      className={`aspect-square rounded-none overflow-hidden cursor-pointer border-2 ${
                        selectedImage === product.image ? 'border-purple-600' : 'border-transparent'
                      }`}
                      onClick={() => setSelectedImage(product.image)}
                    >
                      <img 
                        src={product.image} 
                        alt={`${product.name} huvudbild`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    {product.gallery?.map((img, index) => (
                      <div
                        key={index}
                        className={`aspect-square rounded-none overflow-hidden cursor-pointer border-2 ${
                          selectedImage === img ? 'border-purple-600' : 'border-transparent'
                        }`}
                        onClick={() => setSelectedImage(img)}
                      >
                        <img 
                          src={img} 
                          alt={`${product.name} bild ${index + 2}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  // Video thumbnails
                  product.videos?.map((videoUrl, index) => (
                    <div
                      key={videoUrl}
                      className={`aspect-square rounded-none overflow-hidden cursor-pointer border-2 ${
                        selectedVideoIndex === index ? 'border-purple-600' : 'border-transparent hover:border-purple-400'
                      }`}
                      onClick={() => setSelectedVideoIndex(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedVideoIndex(index);
                        }
                      }}
                      aria-label={`Visa video ${index + 1}`}
                    >
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <Video className="text-white" size={24} />
                      </div>
                    </div>
                  ))
                )}
                
                {/* Show video thumbnail in image mode */}
                {!showVideos && hasVideos && (
                  <div
                    className="aspect-square rounded-none overflow-hidden cursor-pointer border-2 border-transparent hover:border-purple-600 bg-gray-800 flex items-center justify-center"
                    onClick={() => setShowVideos(true)}
                  >
                    <Video className="text-white" size={24} />
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 lg:p-8">
              <div>
                {cannibalizationHint && (
                  <div className="mb-4 rounded-none border border-purple-500/30 bg-purple-950/25 px-4 py-3 text-sm text-slate-300">
                    <p className="leading-relaxed">{cannibalizationHint.message}</p>
                    <Link
                      to={cannibalizationHint.primaryPath}
                      className="mt-2 inline-flex font-medium text-purple-300 underline underline-offset-2 hover:text-purple-200"
                    >
                      {cannibalizationHint.primaryLabel}
                    </Link>
                  </div>
                )}
                <div className="flex justify-between items-start mb-2">
                  <h1 className="text-3xl font-bold text-white">{product.name}</h1>
                  <button 
                    onClick={handleShare}
                    className="text-purple-300 hover:text-purple-200 p-2 rounded-none hover:bg-white/10 transition-colors"
                    title="Dela produkt"
                  >
                    <Share2 size={20} />
                  </button>
                </div>
                <p className="text-gray-400 mb-4">Kategori: {product.category}</p>

                {hasPendingPromo && (
                  <div className="mb-6">
                    <PendingPromoBanner dismissible={false} />
                  </div>
                )}

                <div className="flex items-center mb-6">
                  {product.priceOnRequest ? (
                    <div>
                      <div className="text-2xl font-bold text-purple-300 mb-1">
                        Enligt offert
                      </div>
                      <span className="text-sm text-gray-400">Kontakta oss för pris</span>
                    </div>
                  ) : (
                    <>
                      <PriceWithVatLabel
                        price={product.pricePerDay}
                        className="mr-2"
                        valueClassName="text-2xl font-bold text-purple-600"
                        labelClassName="text-xs text-slate-300"
                      />
                      <span className="text-gray-400">/ dag</span>
                    </>
                  )}
                </div>

                {product.priceOnRequest && product.quoteFormMessage && (
                  <div className="mb-6 p-4 bg-purple-500/20 border border-purple-500/30 rounded-none">
                    <p className="text-purple-300 text-sm">{product.quoteFormMessage}</p>
                  </div>
                )}

                {product.priceOnRequest && (
                  <div className="mb-6 p-4 bg-blue-500/20 border border-blue-500/30 rounded-none">
                    <div className="flex items-start">
                      <FileText className="text-blue-400 mr-3 mt-0.5 shrink-0" size={20} />
                      <div>
                        <h3 className="text-white font-semibold mb-1">Vi skickar offert inom 24 timmar</h3>
                        <p className="text-gray-300 text-sm">
                          Fyll i dina uppgifter och beskriv ditt event så återkommer vi med en skräddarsydd offert.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="max-w-none mb-8">
                  <div className="text-white leading-relaxed !text-white">
                    {formatExactText(product.description)}
                  </div>
                </div>
                
                {product.includes && product.includes.length > 0 && (
                  <details className="mb-8 group" open>
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-2 text-xl font-bold text-white mb-4 select-none [&::-webkit-details-marker]:hidden">
                      <span>Detta ingår</span>
                      <ChevronDown
                        size={20}
                        className="text-purple-300 shrink-0 transition-transform group-open:rotate-180"
                      />
                    </summary>
                    <ul className="space-y-2">
                      {product.includes.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="text-green-600 mt-1 mr-2 shrink-0" size={18} />
                          <span className="text-gray-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {product.persons_capacity && (
                    <div className="bg-slate-800/25 border border-slate-700 p-4">
                      <div className="flex items-center mb-2">
                        <Users className="text-purple-600 mr-2" size={20} />
                        <h3 className="font-medium text-white">Kapacitet</h3>
                      </div>
                      <p className="text-sm text-gray-300">Upp till {product.persons_capacity} personer</p>
                    </div>
                  )}
                  <div className="bg-slate-800/25 border border-slate-700 p-4">
                    <div className="flex items-center mb-2">
                      <Clock className="text-purple-600 mr-2" size={20} />
                      <h3 className="font-medium text-white">Installation</h3>
                    </div>
                    <p className="text-sm text-gray-300">{product.setupTime || "Ca 30 minuter"}</p>
                  </div>
                  <div className="bg-slate-800/25 border border-slate-700 p-4">
                    <div className="flex items-center mb-2">
                      <Package className="text-purple-600 mr-2" size={20} />
                      <h3 className="font-medium text-white">Storlek</h3>
                    </div>
                    <p className="text-sm text-gray-300">{product.size || "Kompakt, passar i de flesta bilar"}</p>
                  </div>
                </div>

                {product.category === SCEN_TROSS_CATEGORY && <ProductScenFaqSection />}

                <div className="flex flex-col sm:flex-row gap-4">
                  {product.priceOnRequest ? (
                    <button
                      onClick={() => {
                        trackQuoteCtaClick(product.id);
                        setShowQuoteForm(true);
                      }}
                      className="flex-1 inline-flex justify-center items-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-none transition-all duration-300 shadow-lg"
                    >
                      <FileText className="mr-2" size={20} />
                      Begär offert
                    </button>
                  ) : (
                    <Link
                      to={`/booking/${product.id}`}
                      onClick={() => trackBookingCtaClick(product, undefined, { entry_point: 'pdp' })}
                      className="flex-1 inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-6 rounded-none transition-colors duration-300"
                    >
                      <Calendar className="mr-2" size={20} />
                      Boka nu
                    </Link>
                  )}

                  <button
                    onClick={handleShare}
                    className="inline-flex justify-center items-center bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-6 rounded-none transition-colors duration-300 border border-white/20"
                  >
                    <Share2 className="mr-2" size={20} />
                    Dela produkt
                  </button>
                </div>
                
                <div className="mt-6">
                  <SocialShareWidget 
                    url={productUrl}
                    title={`Kolla in ${product.name} på festutrustning.se!`}
                    description={product.shortDescription}
                    imageUrl={product.image}
                    compact={true}
                    className="justify-end"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-12 relative z-10">
          <RelatedProducts 
            currentProductId={product.id} 
            category={product.category} 
          />
        </div>
      </div>
      
      {/* Custom animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(120deg); }
          66% { transform: translateY(10px) rotate(240deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
      
      {showShareModal && (
        <ShareProductModal
          productName={product.name}
          productUrl={productUrl}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showQuoteForm && (
        <CustomerQuoteRequestForm
          product={product}
          onClose={() => setShowQuoteForm(false)}
          defaultEventDate={quotePrefill.defaultEventDate}
          defaultDateUndecided={quotePrefill.defaultDateUndecided}
          defaultEventType={quotePrefill.defaultEventType}
        />
      )}
    </div>
  );
};

export default ProductDetailPage;